<script setup>
  import {
    computed,
    nextTick,
    onMounted,
    ref,
    watch
  } from 'vue'

  import { useI18n } from '@shared/i18n.js'
  import { useAjax } from '@composables/useAjax.js'
  import { usePropertySearch } from '@composables/usePropertySearch.js'

  const companies = ref([])
  const list = ref([])
  const mode = ref('railways')

  const SELECTOR = '#fixed_contact_links'

  const { isEn, t, vt } = useI18n('properties.train.js')
  const { callGet } = useAjax()

  const {
    location,
    optionsPrefectures,
    railways,
    stations,
    type,
    propertiesBaseUrl,
    propertiesUrl,
  } = usePropertySearch({
    afterGetPrefectures: () => initializeSelect()
  })

  const {
    stations_path: stationsPath
  } = window.app.page

  location.value = window.app.page.location
  type.value = window.app.page.type

  onMounted(() => {
    initializeSelect()
    getCompanies()
  })

  // 多分SumoSelectを使用しているせいでinputイベントが取れない
  watch(location, () => getCompanies())
  watch(type, () => getCompanies())

  const subtitle = computed(() => mode.value == 'stations' ? vt('please_select_stations') : vt('please_select_train_lines'))

  /**
   * 一覧の取得
   */
  const getCompanies = async () => {
    mode.value = 'railways'
    const url = new URL([window.app.page.root_path, type.value, location.value].join('/'))
    history.pushState(null, '', url.toString())
    url.pathname = `${url.pathname.replace(/\/$/, '')}.json`
    url.searchParams.append('u', window.app.common.cacheKey[type.value])
    const response = await callGet(url.toString())

    companies.value = response.companies ? response.companies : []
    railways.value = []
    stations.value = []
  }

  /**
   * 路線クリック
   */
  const handleClickRailway = (railway) => {
    railways.value = [railway.id]
    submitRailways()
  }

  /**
   * 路線クリック
   */
  const handleClickRailwayStations = (railway) => {
    stations.value = railway.stations.filter((station) => station.properties_count > 0).map((station) => station.id)
    nextTick(() => submit())
  }

  /**
   * 運営会社の全選択／解除
   */
  const handleInputCompany = (e, company) => {
    setFooterMenu(railways)
    if (e.target.checked) {
      const current = new Set(railways.value)
      company.railways.filter((railway) => !current.has(railway.id) && railway.properties_count > 0).forEach((railway) => railways.value.push(railway.id))
    }
    else {
      const set = new Set(company.railways.map((railway) => railway.id))
      railways.value = railways.value.filter((id) => !set.has(id))
    }
  }

  /**
   * 入力操作
   */
  const handleInputRailway = () => setFooterMenu(railways)

  /**
   * 路線の全選択／解除
   */
  const handleInputRailwayStations = (e, railway) => {
    setFooterMenu(stations)
    if (e.target.checked) {
      const current = new Set(stations.value)
      railway.stations.filter((station) => !current.has(station.id) && station.properties_count > 0).forEach((station) => stations.value.push(station.id))
    }
    else {
      const set = new Set(railway.stations.map((station) => station.id))
      stations.value = stations.value.filter((id) => !set.has(id))
    }
  }

  /**
   * 入力操作
   */
  const handleInputStation = () => setFooterMenu(stations)

  /**
   * 選択肢の初期化
   */
  const initializeSelect = () => {
    if (optionsPrefectures.value.length > 0) {
      nextTick(() => $("#main .sumo_select select").SumoSelect())
    }
  }

  /**
   * 下部メニューの表示制御
   */
  const setFooterMenu = (data) => {
    const count = data.value.length
    setTimeout(() => {
      if (count == 0 && data.value.length > 0) {
        $(SELECTOR).fadeIn()
      }
      else if (count > 0 && data.value.length == 0) {
        $(SELECTOR).fadeOut()
      }
    }, 100)
  }

  /**
   * 検索実行
   */
  const submit = () => {
    window.location.href = propertiesUrl()
  }

  /**
   * 駅の検索実行
   */
  const submitRailways = async () => {
    if (railways.length == 0) return
    $(SELECTOR).fadeOut()

    const url = new URL(window.location.href)
    url.pathname = stationsPath
    url.searchParams.append('type', type.value)
    url.searchParams.append('prefecture', location.value)
    url.searchParams.append('railways', railways.value.join(','))
    url.searchParams.append('u', window.app.common.cacheKey[type.value])
    const response = await callGet(url.toString())

    mode.value = 'stations'
    list.value = response.railways ? response.railways : []
    stations.value = []
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }
</script>

<template>
  <div id="body">
    <div id="main">
      <div class="head">
        <h1 class="c-ttl">{{ vt('title') }}</h1>
      </div>
      <div v-show="optionsPrefectures.length > 0" class="sumo_select" id="search_op">
        <div class="container">
          <div class="toggle_contents_header">Search for Properties</div>
          <div class="c-sumoselect selects selects_pc sumo_select toggle_contents toggle_contents_flex">
            <div class="inner">
              <div class="select_box">
                <label class="select_type_a properties-type">
                  <select v-model="type" class="search-properties-type" tabindex="-1">
                    <option value="sale">{{ t(isEn ? 'c.for_sale.other' : 'c.for_sale') }}</option>
                    <option value="rent">{{ t(isEn ? 'c.for_rent.other' : 'c.for_rent') }}</option>
                  </select>
                </label>

                <label class="select_type_a location">
                  <p>{{ vt('select_prefecture') }}</p>
                  <select v-model="location" class="search-property-type-detail select_pc" placeholder="Prefecture" tabindex="-1" name="location">
                    <option></option>
                    <option v-for="option in optionsPrefectures" :key="`prefecture-${option.value}`" :value="option.en">{{ option.label }}</option>
                  </select>
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="l-wrap">
        <div class="p-search__block">
          <h2 class="sec-title">{{ subtitle }}</h2>
          <div class="frame">
            <template v-if="mode == 'railways'">
              <template v-for="(company, index) in companies" :key="`company-${index}`">
                <div class="p-search-ttl">
                  <input v-model="company.selected" :id="`company-${index}`" :disabled="company.properties_count == 0" type="checkbox" @click="(e) => handleInputCompany(e, company)">
                  <label class="checkbox" :for="`company-${index}`">
                    <span>
                      {{ company.name }}({{ company.properties_count }})
                    </span>
                  </label>
                </div>
                <ul class="selector">
                  <li v-for="railway in company.railways" :key="`railway-${railway.id}`">
                    <input v-model="railways" :id="`railway-${railway.id}`" :disabled="railway.properties_count == 0" type="checkbox" :value="railway.id" @click="handleInputRailway">
                    <label class="checkbox" :for="`railway-${railway.id}`">
                      <template v-if="railway.properties_count > 0">
                        <a @click="handleClickRailway(railway)">
                          {{ railway.name }}({{ railway.properties_count }})
                        </a>
                      </template>
                      <template v-else>
                        <span>{{ railway.name }}({{ railway.properties_count }})</span>
                      </template>
                    </label>
                  </li>
                </ul>
              </template>
            </template>
            <template v-else-if="mode == 'stations'">
              <template v-for="(railway, index) in list" :key="`railway-stations-${index}`">
                <div class="p-search-ttl">
                  <input v-model="railway.selected" :id="`railway-${railway.id}`" :disabled="railway.properties_count == 0" type="checkbox" @click="(e) => handleInputRailwayStations(e, railway)">
                  <label class="checkbox" :for="`railway-${railway.id}`">
                    <template v-if="railway.properties_count > 0">
                      <a @click.prevent="handleClickRailwayStations(railway)">
                        {{ railway.name }}({{ railway.properties_count }})
                      </a>
                    </template>
                    <template v-else>
                      <span>{{ railway.name }}({{ railway.properties_count }})</span>
                    </template>
                  </label>
                </div>
                <ul class="selector">
                  <li v-for="station in railway.stations" :key="`station-${station.id}`">
                    <input v-model="stations" :id="`station-${station.id}`" :disabled="station.properties_count == 0" type="checkbox" :value="station.id" @click="handleInputStation">
                    <label class="checkbox" :for="`station-${station.id}`">
                      <template v-if="station.properties_count > 0">
                        <a :href="`${propertiesBaseUrl}/station${station.id}`">
                          {{ station.name }}({{ station.properties_count }})
                        </a>
                      </template>
                      <template v-else>
                        <span>{{ station.name }}({{ station.properties_count }})</span>
                      </template>
                    </label>
                  </li>
                </ul>
              </template>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>
  <div class="content_small" id="fixed_contact_links">
    <a v-if="mode == 'railways'" class="btn" @click="submitRailways">{{ vt('select_stations') }}</a>
    <a v-else-if="mode == 'stations'" class="btn" @click="submit">{{ t('c.search') }}</a>
  </div>
</template>
